// src/constants/permissions.constants.ts
/**
 * Permission constants - Centralized permission strings
 *
 * Format: MODULE.MENU.ACTION
 * Example: "products.product-catalog.view"
 *
 * Benefits:
 * - Type safety (autocomplete)
 * - Single source of truth
 * - Easy refactoring
 * - Prevents typos
 */

// Home / Inicio Module (dashboard KPIs — independent from reports.statistics)
export const HOME_PERMISSIONS = {
  VIEW: 'home.dashboard.view',
  /** Barra de pendientes de Inicio (independiente del dashboard global) */
  PENDING_WORK_VIEW: 'home.pending-work.view',
  /** "Mi actividad" en Inicio: ventas propias del usuario */
  MY_ACTIVITY_VIEW: 'home.my-activity.view',
} as const;

// Products Module
export const PRODUCT_PERMISSIONS = {
  VIEW: 'products.product-catalog.view',
  VIEW_ROW: 'products.product-catalog.view_row',
  ADD: 'products.product-catalog.add',
  EDIT: 'products.product-catalog.edit',
  DELETE: 'products.product-catalog.delete',
  DUPLICATE: 'products.product-catalog.duplicate',
  EXPORT: 'products.product-catalog.export',
  /** Columna Proveedor en la tabla del catálogo */
  COLUMN_SUPPLIER: 'products.product-catalog.column_supplier',
} as const;

// Inventory Module
export const INVENTORY_PERMISSIONS = {
  REGISTER: {
    VIEW: 'inventory.inventory-register.view',
    ADD: 'inventory.inventory-register.add',
    EDIT: 'inventory.inventory-register.edit',
    VIEW_ROW: 'inventory.inventory-register.view_row',
    EXPORT: 'inventory.inventory-register.export',
    CANCEL: 'inventory.inventory-register.cancel',
    /** Vista impresa del detalle del registro (no etiquetas de producto). */
    PRINT_DETAIL: 'inventory.inventory-register.print',
    /** Datos e impresión de etiquetas de unidades del registro. */
    LABEL_PRINT: 'inventory.inventory-register.label_print',
  },
  WAREHOUSE: {
    VIEW: 'inventory.inventory-warehouse.view',
    VIEW_ROW: 'inventory.inventory-warehouse.view_row',
    EDIT: 'inventory.inventory-warehouse.edit',
    TRANSFER: 'inventory.inventory-warehouse.transfer',
    ADJUST: 'inventory.inventory-warehouse.adjust',
    KPI_TOTAL_PRODUCTS: 'inventory.inventory-warehouse.kpi_total_products',
    KPI_TOTAL_SALE_VALUE: 'inventory.inventory-warehouse.kpi_total_sale_value',
    KPI_TOTAL_COST_VALUE: 'inventory.inventory-warehouse.kpi_total_cost_value',
    KPI_UNASSIGNED: 'inventory.inventory-warehouse.kpi_unassigned',
    KPI_ASSIGNED: 'inventory.inventory-warehouse.kpi_assigned',
    KPI_IN_TRANSIT: 'inventory.inventory-warehouse.kpi_in_transit',
    KPI_BAJAS: 'inventory.inventory-warehouse.kpi_bajas',
    KPI_WAREHOUSE_RESERVED: 'inventory.inventory-warehouse.kpi_warehouse_reserved',
    RESERVE: 'inventory.inventory-warehouse.reserve',
    UNRESERVE: 'inventory.inventory-warehouse.unreserve',
  },
  TRANSFER: {
    VIEW: 'inventory.inventory-transfer.view',
    VIEW_ROW: 'inventory.inventory-transfer.view_row',
    APPROVE: 'inventory.inventory-transfer.approve',
    REJECT: 'inventory.inventory-transfer.reject',
    CREATE: 'inventory.inventory-transfer.create',
    /** Conteo manual obligatorio al aceptar (toggle en modal de confirmación) */
    TOGGLE_REQUIRE_COUNTING: 'inventory.inventory-transfer.toggle_require_counting',
    /** Campo: Monto Total de Costo (detalles / aceptación / rechazo / impresión) */
    SHOW_TOTAL_COST_AMOUNT: 'inventory.inventory-transfer.show_total_cost_amount',
    /** Campo: Monto Total de Venta (detalles / aceptación / rechazo / impresión) */
    SHOW_TOTAL_SALE_AMOUNT: 'inventory.inventory-transfer.show_total_sale_amount',
    /**
     * Legacy catalog code — export is granted by VIEW (menu-only for export).
     * Prefer VIEW; kept for reference / old role rows.
     */
    EXPORT: 'inventory.inventory-transfer.export',
  },
  REQUEST: {
    VIEW: 'inventory.inventory-request.view',
    APPROVE: 'inventory.inventory-request.approve',
  },
  BRANCH: {
    VIEW: 'inventory.inventory-branch.view',
    VIEW_ROW: 'inventory.inventory-branch.view_row',
    EDIT: 'inventory.inventory-branch.edit',
    TRANSFER: 'inventory.inventory-branch.transfer',
    RESERVE: 'inventory.inventory-branch.reserve',
    UNRESERVE: 'inventory.inventory-branch.unreserve',
    KPI_TOTAL_PRODUCTS: 'inventory.inventory-branch.kpi_total_products',
    KPI_TOTAL_SALE_VALUE: 'inventory.inventory-branch.kpi_total_sale_value',
    KPI_TOTAL_COST_VALUE: 'inventory.inventory-branch.kpi_total_cost_value',
    KPI_BAJAS: 'inventory.inventory-branch.kpi_bajas',
  },
  /** Inventario general — consulta por pieza con filtros almacén + sucursal */
  GENERAL: {
    VIEW: 'inventory.inventory-general.view',
    VIEW_ROW: 'inventory.inventory-general.view_row',
    CREATE: 'inventory.inventory-general.create',
    KPI_TOTAL_PRODUCTS: 'inventory.inventory-general.kpi_total_products',
    KPI_TOTAL_SALE_VALUE: 'inventory.inventory-general.kpi_total_sale_value',
    KPI_TOTAL_COST_VALUE: 'inventory.inventory-general.kpi_total_cost_value',
    KPI_UNASSIGNED: 'inventory.inventory-general.kpi_unassigned',
    KPI_ASSIGNED: 'inventory.inventory-general.kpi_assigned',
    KPI_IN_TRANSIT: 'inventory.inventory-general.kpi_in_transit',
    KPI_BAJAS: 'inventory.inventory-general.kpi_bajas',
    KPI_CATALOG_PRODUCT_TOTAL: 'inventory.inventory-general.kpi_catalog_product_total',
  },
  SAFETY_STOCK: {
    VIEW: 'inventory.safety-stock.view',
    EDIT: 'inventory.safety-stock.edit',
  },
  PHYSICAL_COUNT: {
    VIEW: 'inventory.physical-count.view',
    CREATE: 'inventory.physical-count.create',
    EXPORT: 'inventory.physical-count.export',
    WRITE_OFF_MISSING: 'inventory.physical-count.write-off-missing',
  },
} as const;

// Orders/Sales Module
export const ORDERS_PERMISSIONS = {
  VIEW: 'orders.sales-management.view',
  VIEW_ROW: 'orders.sales-management.view_row',
  PAY: 'orders.sales-management.pay',
  RETURN: 'orders.sales-management.return',
  EXCHANGE: 'orders.sales-management.exchange',
  /** Editar venta / marcar entrega al cliente (PATCH delivery) */
  EDIT: 'orders.sales-management.edit',
  /** Botón Añadir / nueva venta (Gestión de Ventas) */
  ADD: 'orders.sales-management.add',
  /** Fecha comercial al registrar venta (nueva venta) */
  MODIFY_SALE_DATE: 'orders.sales-management.modify_sale_date',
  /** Corregir fecha/hora comercial de una venta ya registrada */
  EDIT_SALE_DATE: 'orders.sales-management.edit_sale_date',
  SALES_CATALOG_VIEW: 'orders.sales-catalog.view',
  SALES_BY_PIECE_VIEW: 'orders.sales-by-piece.view',
  /**
   * Venta multi-sucursal (rol): habilita el catálogo de otras sucursales al armar la venta
   * y confirmar la venta mixta (POST /sales-orders/mixed). El cierre de logística
   * (envío completado) ocurre automáticamente al recibir el último traslado.
   */
  MIXED_SELL: 'orders.sales-management.mixed_sell',
  /** Permite elegir otro usuario de la misma sucursal como vendedor al registrar una venta */
  CHANGE_SELLER: 'orders.sales-management.change_seller',
  /** Menú "Gestión de Vendedores" (permiso único: todo o nada) */
  SELLER_MANAGEMENT_VIEW: 'orders.seller-management.view',
} as const;

// CRM Module
export const CRM_PERMISSIONS = {
  VIEW: 'crm.client-management.view',
  ADD: 'crm.client-management.add',
  EDIT: 'crm.client-management.edit',
  DELETE: 'crm.client-management.delete',
  DELETE_ROW: 'crm.client-management.delete_row',
  EXPORT: 'crm.client-management.export',
} as const;

// Reports Module
export const REPORTS_PERMISSIONS = {
  PRODUCT_HISTORY: 'reports.product-history.view',
  STATISTICS: 'reports.statistics.view',
} as const;

// Settings Module
export const SETTINGS_PERMISSIONS = {
  PROPERTIES: {
    VIEW: 'settings.properties.view',
    ADD: 'settings.properties.add',
    EDIT: 'settings.properties.edit',
    DELETE: 'settings.properties.delete',
  },
  ROLES: {
    /** Menu-only: view unlocks the full Roles screen (add/edit/delete/block). */
    VIEW: 'settings.roles-management.view',
    /** @deprecated Not assignable — use VIEW (menu-only). */
    ADD: 'settings.roles-management.add',
    /** @deprecated Not assignable — use VIEW (menu-only). */
    EDIT: 'settings.roles-management.edit',
    /** @deprecated Not assignable — use VIEW (menu-only). */
    DELETE: 'settings.roles-management.delete',
    /** @deprecated Not assignable — use VIEW (menu-only). */
    BLOCK: 'settings.roles-management.block',
  },
  USERS: {
    /** Menu-only: view unlocks the full Users screen (add/export/delete). */
    VIEW: 'settings.users-management.view',
    /** @deprecated Not assignable — use VIEW (menu-only). */
    ADD: 'settings.users-management.add',
    /** @deprecated Not assignable — use VIEW (menu-only). */
    EDIT: 'settings.users-management.edit',
    /** @deprecated Not assignable — use VIEW (menu-only). */
    DELETE: 'settings.users-management.delete',
    /** @deprecated Not assignable — use VIEW (menu-only). */
    EXPORT: 'settings.users-management.export',
    /** @deprecated Not assignable — use VIEW (menu-only). */
    BLOCK: 'settings.users-management.block',
  },
} as const;

// System Module
export const SYSTEM_PERMISSIONS = {
  VIEW: 'system.system-config.view',
  EDIT: 'system.system-config.edit',
  /** Permite alternar modo claro / oscuro en la barra superior */
  SELECT_THEME: 'system.system-config.select_theme',
} as const;

/**
 * Flags globales de Visibilidad de datos (detalle pieza / producto).
 * Opción A = UI-only: hide fields in the client; APIs still return the values.
 */
export const DATA_VISIBILITY_PERMISSIONS = {
  COST_UNIT_PRICE: 'system.data-visibility.cost_unit_price',
  COST_PER_GRAM: 'system.data-visibility.cost_per_gram',
  SALE_PRICE: 'system.data-visibility.sale_price',
  SALE_PRICE_PER_GRAM: 'system.data-visibility.sale_price_per_gram',
  FINAL_PRICE: 'system.data-visibility.final_price',
  PROFIT: 'system.data-visibility.profit',
  MARGIN: 'system.data-visibility.margin',
  SUPPLIER_NAME: 'system.data-visibility.supplier_name',
  SUPPLIER_STATUS: 'system.data-visibility.supplier_status',
  FILTER_SUPPLIER: 'system.data-visibility.filter_supplier',
  FILTER_SUPPLIER_CODE: 'system.data-visibility.filter_supplier_code',
} as const;

/**
 * Navigation-specific permissions
 * These are the permissions used to control navigation menu visibility
 */
export const NAVIGATION_PERMISSIONS = {
  HOME: HOME_PERMISSIONS.VIEW,
  PRODUCTS: PRODUCT_PERMISSIONS.VIEW,
  INVENTORY_REGISTER: INVENTORY_PERMISSIONS.REGISTER.VIEW,
  INVENTORY_WAREHOUSE: INVENTORY_PERMISSIONS.WAREHOUSE.VIEW,
  INVENTORY_TRANSFER: INVENTORY_PERMISSIONS.TRANSFER.VIEW,
  INVENTORY_REQUEST: INVENTORY_PERMISSIONS.REQUEST.VIEW,
  INVENTORY_BRANCH: INVENTORY_PERMISSIONS.BRANCH.VIEW,
  INVENTORY_GENERAL: INVENTORY_PERMISSIONS.GENERAL.VIEW,
  INVENTORY_SAFETY_STOCK: INVENTORY_PERMISSIONS.SAFETY_STOCK.VIEW,
  INVENTORY_PHYSICAL_COUNT: INVENTORY_PERMISSIONS.PHYSICAL_COUNT.VIEW,
  SALES: ORDERS_PERMISSIONS.VIEW,
  SALES_BY_PIECE: ORDERS_PERMISSIONS.SALES_BY_PIECE_VIEW,
  SELLER_MANAGEMENT: ORDERS_PERMISSIONS.SELLER_MANAGEMENT_VIEW,
  CRM_CLIENT_MANAGEMENT: CRM_PERMISSIONS.VIEW,
  REPORTS_PRODUCT_HISTORY: REPORTS_PERMISSIONS.PRODUCT_HISTORY,
  REPORTS_STATISTICS: REPORTS_PERMISSIONS.STATISTICS,
  SETTINGS_PROPERTIES: SETTINGS_PERMISSIONS.PROPERTIES.VIEW,
  SETTINGS_ROLES: SETTINGS_PERMISSIONS.ROLES.VIEW,
  SETTINGS_USERS: SETTINGS_PERMISSIONS.USERS.VIEW,
  SYSTEM: SYSTEM_PERMISSIONS.VIEW,
} as const;

// Type helper to extract permission string types
export type PermissionConstant =
  | (typeof HOME_PERMISSIONS)[keyof typeof HOME_PERMISSIONS]
  | (typeof PRODUCT_PERMISSIONS)[keyof typeof PRODUCT_PERMISSIONS]
  | (typeof INVENTORY_PERMISSIONS)[keyof typeof INVENTORY_PERMISSIONS][keyof (typeof INVENTORY_PERMISSIONS)[keyof typeof INVENTORY_PERMISSIONS]]
  | (typeof ORDERS_PERMISSIONS)[keyof typeof ORDERS_PERMISSIONS]
  | (typeof CRM_PERMISSIONS)[keyof typeof CRM_PERMISSIONS]
  | (typeof REPORTS_PERMISSIONS)[keyof typeof REPORTS_PERMISSIONS]
  | (typeof SETTINGS_PERMISSIONS)[keyof typeof SETTINGS_PERMISSIONS][keyof (typeof SETTINGS_PERMISSIONS)[keyof typeof SETTINGS_PERMISSIONS]]
  | (typeof SYSTEM_PERMISSIONS)[keyof typeof SYSTEM_PERMISSIONS]
  | (typeof DATA_VISIBILITY_PERMISSIONS)[keyof typeof DATA_VISIBILITY_PERMISSIONS];
